import React, { useContext, useMemo } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import FocusLock from 'react-focus-lock';
import LocationSelector from '../../controls/LocationSelector';
import NavigationHeader from '../../elements/NavigationHeader';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import { notifyFullMode, notifyExitFullMode } from '../../containers/_commons/navigation';
import { MobileFooter } from '../../lab/MobileFilterView';
import { Button, Inline } from '@front10/base-elements';
import Box from '../../elements/Box';
import Icon from '../../elements/Icon';
import Text from '../../elements/Text';

const Autocomplete = ({
  placeholderKey = 'cmp-autocomplete-placeholder',
  value,
  options = [],
  onEnterKey = () => {},
  dispatch = () => {},
  hideMenuWhenUserTyping = true,
  memoryPrompt,
  isMobile = false,
  headerSettings,
  titleLabelKey = 'cmp-header-mobile-autocomplete',
  containerFullScreen = {
    bgColor: 'white'
  },
  buttonClearLabelKey = 'cmp-autocomplete-clear-button',
  buttonDoneLabelKey = 'cmp-autocomplete-done-button',
  searchRecommendation,
  descriptions,
  showDescriptions,
  goToFullScreen,
  renderCtaButton,
  showCTA,
  ...props
}) => {
  const { translate, direction, theme } = useContext(Context);
  const [isFullScreen, setFullScreen] = React.useState(false);
  const doneButtonRef = React.useRef(null);
  const [activateDoneButton, setActivateDoneButton] = React.useState(false);
  const [promptChange, setPromptChange] = React.useState(false);

  const [optionVirtual, setOptionVirtual] = React.useState({
    label: props?.defaultValue || '',
    value: props?.defaultValue || '',
    virtual: true
  });

  const promptSuggestion = useMemo(() => {
    return options.map((option) => ({
      label: translate(option.label),
      value: translate(option.label),
      ...option
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translate, options]);

  const optionsMerged = useMemo(() => {
    const mergedOptions = [...memoryPrompt, ...promptSuggestion];
    return mergedOptions;
  }, [promptSuggestion, memoryPrompt]);

  React.useEffect(() => {
    setOptionVirtual({
      label: props?.defaultValue,
      value: props?.defaultValue,
      virtual: true
    });
  }, [props?.defaultValue, props?.lastValue, props?.value]);

  const handleChange = () => {
    if (optionVirtual.label !== props?.defaultValue) {
      setOptionVirtual({
        label: props?.defaultValue,
        value: props?.defaultValue,
        virtual: true
      });
    }
  };

  const newProps = React.useMemo(() => {
    return {
      ...props,
      menuItem: {
        ...props?.menuItem,
        style: {
          ...props?.menuItem?.style,
          display: value ? 'none' : 'unset'
        }
      },
      inputItem: {
        ...props?.inputItem,
        className:
          value !== ''
            ? `${props?.inputItem?.className} fr-autocomplete-input-value-class`
            : props?.inputItem?.className
      }
    };
  }, [value, props]);

  const onKeyDown = (e) => {
    if (e.keyCode === 13) {
      if (isMobile && isFullScreen) {
        goBack();
      }
      handleChange();
      onEnterKey();
    }
  };

  const renderItem = (item) => {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" {...props?.listItem}>
        {item.icon ? (
          <Icon icon={item.icon} size={20} marginRight="4px" {...item?.iconStyles} />
        ) : null}
        <Text>{item.value}</Text>
      </Box>
    );
  };

  const handleFocus = () => {
    if (isMobile && goToFullScreen && !isFullScreen) {
      notifyFullMode();
      setFullScreen(true);
    }
  };

  const goBack = () => {
    if (isMobile && isFullScreen) {
      notifyExitFullMode();
      setFullScreen(false);
    }
  };

  const autocomplete = (
    <LocationSelector
      charactersAllowed="all"
      flexGrow="1"
      showLoadingMessage={true}
      showSelectorMenuArrow={false}
      showLabelInside={false}
      clearable={true}
      showCode={false}
      searchInside={false}
      showArrowsIfClearable={false}
      showArrows={false}
      placeholder={translate(placeholderKey, 'Cheapest fare to Tokyo next month')}
      {...newProps}
      noResultItem={props?.listItem}
      options={
        hideMenuWhenUserTyping && (Boolean(value) || isMobile)
          ? [optionVirtual]
          : [...optionsMerged, optionVirtual]
      }
      defaultValue={value}
      onBlur={handleChange}
      onKeyDown={onKeyDown}
      onChange={(item) => {
        setPromptChange(true);
        dispatch({ type: 'SELECT_SUGGESTION', payload: { prompt: item.label } });
        props?.onChange(item.value);
      }}
      itemRenderer={renderItem}
      onInputChange={(inputValue) => {
        setPromptChange(true);
        if (inputValue) {
          props?.onChange(inputValue);
        } else {
          props?.onClear();
        }
      }}
      selectOptionByDefault={false}
      onFocus={handleFocus}
    />
  );

  if (props?.openOnFocus && isFullScreen && isMobile) {
    return (
      <FocusLock autoFocus returnFocus>
        <Box
          cmp-styles={{
            height: '100vH',
            width: '100%',
            position: 'fixed',
            top: '0',
            left: '0',
            'z-index': '2'
          }}
          {...containerFullScreen}
        >
          <NavigationHeader
            title={translate(titleLabelKey)}
            onClickIcon={goBack}
            styleIcon={{
              transform: 'rotate(90deg)',
              display: 'inline-block'
            }}
            {...headerSettings}
            focusBehavior
          />
          <Box paddingWidth="12px">
            {autocomplete}
            {showDescriptions && descriptions}
            {showCTA && renderCtaButton({ goBack })}
            <Box className="options-list-items">
              {optionsMerged.map((option) => {
                return (
                  <Box
                    tabIndex={0}
                    onClick={() => {
                      if (value !== option.value) {
                        setPromptChange(true);
                        dispatch({ type: 'SELECT_SUGGESTION', payload: { prompt: option.value } });
                        props?.onChange(option.value);
                      }
                    }}
                    onKeyDown={(evt) => {
                      if ((evt.key === 'Enter' || evt.keyCode === 13) && value !== option.value) {
                        setPromptChange(true);
                        dispatch({ type: 'SELECT_SUGGESTION', payload: { prompt: option.value } });
                        props?.onChange(option.value);
                      }
                    }}
                    {...props?.menuItem}
                  >
                    {renderItem(option)}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <EmotionCache direction={direction}>
            <ThemeProvider theme={theme?.designTokens ?? undefined}>
              <MobileFooter align="end" sx={{ width: '100%', right: 0, p: '10px' }}>
                <Inline gap={2} align="end">
                  <Button
                    data-testId="buttonFooterClear"
                    type="button"
                    disabled={Boolean(value) ? false : true}
                    variant="text"
                    onClick={() => {
                      props?.onChange('');
                      setActivateDoneButton(true);
                      doneButtonRef?.current?.focus();
                    }}
                    className="footerButtons"
                    {...headerSettings?.buttonClear}
                  >
                    {translate(buttonClearLabelKey, 'Clear')}
                  </Button>
                  <Button
                    data-testId={'buttonFooterDone'}
                    ref={doneButtonRef}
                    disabled={!Boolean(value) && !activateDoneButton ? true : false}
                    onClick={() => {
                      goBack();
                      if (value && promptChange) {
                        searchRecommendation();
                      }
                      setPromptChange(false);
                      setActivateDoneButton(false);
                    }}
                    type="button"
                    variant="text"
                    {...headerSettings?.buttonDone}
                  >
                    {translate(buttonDoneLabelKey, 'Done')}
                  </Button>
                </Inline>
              </MobileFooter>
            </ThemeProvider>
          </EmotionCache>
        </Box>
      </FocusLock>
    );
  } else {
    return (
      <>
        {autocomplete}
        {showDescriptions && descriptions}
      </>
    );
  }
};

export default Autocomplete;
